// src/components/bot/conversationManager.js
// ─────────────────────────────────────────────────────────────────────────────
// Layer 3 — Conversation Manager
//
// Responsibilities:
//   • Maintain the canonical conversation history as a module-level array.
//   • Enforce a maximum history window to prevent token bloat.
//   • Convert history to Gemini's `contents[]` format for multi-turn prompts.
//   • Guarantee history is always properly alternating (user → model → user …)
//     so Gemini never receives two consecutive turns of the same role.
//
// Design decision: module-level state (not React state) survives component
// re-renders and hot reloads. The session boundary is page reload.
// ─────────────────────────────────────────────────────────────────────────────

// Maximum number of complete turns (user + model pairs) to retain.
// 10 pairs = 20 history entries, which is roughly 3–5k tokens with typical messages.
const MAX_TURNS = 10;

/** @type {Array<{ role: 'user' | 'model', text: string }>} */
let _history = [];

// ── Mutation API ──────────────────────────────────────────────────────────────

/**
 * Append the user's message to conversation history.
 * Called BEFORE the AI generates a response.
 *
 * @param {string} text
 */
export function appendUser(text) {
  _history.push({ role: 'user', text });
  _trim();
}

/**
 * Append the model's response to conversation history.
 * Called AFTER the AI generates a response (always called, even for error fallbacks,
 * to ensure history remains properly alternating).
 *
 * @param {string} text
 */
export function appendModel(text) {
  _history.push({ role: 'model', text });
  _trim();
}

// ── Read API ──────────────────────────────────────────────────────────────────

/**
 * Convert the stored history to Gemini's `contents[]` format, ready to be
 * included in the request body's `contents` array.
 *
 * IMPORTANT: This must be called AFTER appendUser() for the current turn,
 * but BEFORE the current turn's user message is added to `contents`.
 * It slices off the last entry (the current user message) so the caller
 * can append it separately as the active turn.
 *
 * @returns {Array<{ role: 'user' | 'model', parts: [{ text: string }] }>}
 */
export function toGeminiContents() {
  // Drop the last entry (current user message — just added by appendUser)
  const previousTurns = _history.slice(0, -1);

  // Apply the window limit to previous turns
  const windowed = previousTurns.slice(-(MAX_TURNS * 2));

  // Guarantee proper alternation: Gemini requires user → model → user → model.
  // Walk the windowed history and only keep correctly-sequenced messages.
  const balanced = [];
  let expectRole = 'user';

  for (const msg of windowed) {
    if (msg.role === expectRole) {
      balanced.push(msg);
      expectRole = expectRole === 'user' ? 'model' : 'user';
    }
    // If out-of-sequence (e.g. two consecutive user messages from an error turn),
    // skip it silently — Gemini will not see the orphaned entry.
  }

  return balanced.map(m => ({
    role:  m.role,
    parts: [{ text: m.text }],
  }));
}

/**
 * Return a snapshot of the raw history array (for debugging/testing).
 *
 * @returns {Array<{ role: string, text: string }>}
 */
export function getHistory() {
  return [..._history];
}

/**
 * Reset conversation history.
 * Called when the user explicitly starts a new session (not currently wired to UI,
 * but available for future "Clear chat" feature).
 */
export function reset() {
  _history = [];
}

// ── Internal ──────────────────────────────────────────────────────────────────

/**
 * Trim history to the rolling window.
 * Keeps the last MAX_TURNS * 2 entries, always cutting at a user/model boundary
 * to avoid orphaned entries.
 */
function _trim() {
  const limit = MAX_TURNS * 2;
  if (_history.length > limit) {
    _history = _history.slice(-limit);
  }
}
